import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, DollarSign, Users, Target, ShoppingCart, TrendingDown, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { getCeoDashboard } from "@/lib/dashboardApi";

interface CEOMetrics {
  totalRevenue: number;
  ytdRevenue: number;
  adjustedPeriodRevenue: number;
  avgTicket: number;
  lostValue: number;
  
  overallConversion: number;
  conversionBySales: Record<string, { total: number; converted: number; rate: number }>;
  conversionByClientType: Record<string, { total: number; converted: number; rate: number }>;
  
  serviceMix: {
    tours: number;
    transfers: number;
    attractions: number;
    airportServices: number;
    additionalRequests: number;
  };
  
  topAgencies: Array<{ name: string; total: number; count: number }>;
  topOperators: Array<{ name: string; total: number; count: number }>;
  topFinalClients: Array<{ name: string; total: number; count: number }>;
  
  totalProposals: number;
  convertedCount: number;
  controllableCancellations: number;
  uncontrollableCancellations: number;
  uncontrollableCanceledValue: number;
  uncontrollableCancellationRate: number;
}

const CEODashboard = () => {
  const [metrics, setMetrics] = useState<CEOMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    to: new Date(), // Today
  });
  const [viewMode, setViewMode] = useState<"month" | "lastMonth" | "quarter" | "year" | "custom">("month");

  useEffect(() => {
    fetchMetrics();
  }, [dateRange, viewMode]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      
      let startDate: Date, endDate: Date;
      
      if (viewMode === "month") {
        startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        endDate = new Date();
      } else if (viewMode === "lastMonth") {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        startDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
        endDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
      } else if (viewMode === "quarter") {
        const currentMonth = new Date().getMonth();
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        startDate = new Date(new Date().getFullYear(), quarterStartMonth, 1);
        endDate = new Date();
      } else if (viewMode === "year") {
        startDate = new Date(new Date().getFullYear(), 0, 1);
        endDate = new Date();
      } else {
        startDate = dateRange.from;
        endDate = dateRange.to;
      }
      
      const response = await getCeoDashboard({
        from: format(startDate, "yyyy-MM-dd"),
        to: format(endDate, "yyyy-MM-dd"),
      });

      setMetrics({
        totalRevenue: response.total_revenue || 0,
        ytdRevenue: response.ytd_revenue || 0,
        adjustedPeriodRevenue: response.adjusted_period_revenue || 0,
        avgTicket: response.avg_ticket || 0,
        lostValue: response.lost_value || 0,
        overallConversion: response.overall_conversion || 0,
        conversionBySales: response.conversion_by_sales || {},
        conversionByClientType: response.conversion_by_client_type || {},
        serviceMix: {
          tours: response.service_mix?.tours || 0,
          transfers: response.service_mix?.transfers || 0,
          attractions: response.service_mix?.attractions || 0,
          airportServices: response.service_mix?.airport_services || 0,
          additionalRequests: response.service_mix?.additional_requests || 0,
        },
        topAgencies: response.top_agencies || [],
        topOperators: response.top_operators || [],
        topFinalClients: response.top_final_clients || [],
        totalProposals: response.total_proposals || 0,
        convertedCount: response.converted_count || 0,
        controllableCancellations: response.controllable_cancellations || 0,
        uncontrollableCancellations: response.uncontrollable_cancellations || 0,
        uncontrollableCanceledValue: response.uncontrollable_canceled_value || 0,
        uncontrollableCancellationRate: response.uncontrollable_cancellation_rate || 0,
      });

    } catch (error: any) {
      console.error("Error fetching CEO metrics:", error);
      toast({
        title: "Error loading metrics",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">CEO Dashboard</h2>
          <p className="text-muted-foreground">Loading strategic insights...</p>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const totalServices = Object.values(metrics.serviceMix).reduce((a, b) => a + b, 0);
  const conversionBySalesValue = Object.entries(metrics.conversionBySales).map(([person, data]) => {
    const rate = data.total > 0 ? (data.converted / data.total) * 100 : 0;
    return [person, { ...data, rate }] as const;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">CEO Dashboard</h2>
          <p className="text-muted-foreground">Strategic View and Global Indicators</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <Button
              variant={viewMode === "month" ? "default" : "outline"}
              onClick={() => setViewMode("month")}
            >
              Current Month
            </Button>
            <Button
              variant={viewMode === "lastMonth" ? "default" : "outline"}
              onClick={() => setViewMode("lastMonth")}
            >
              Last Month
            </Button>
            <Button
              variant={viewMode === "quarter" ? "default" : "outline"}
              onClick={() => setViewMode("quarter")}
            >
              Current Quarter
            </Button>
            <Button
              variant={viewMode === "year" ? "default" : "outline"}
              onClick={() => setViewMode("year")}
            >
              Annual
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={viewMode === "custom" ? "default" : "outline"}>
                  <Calendar className="mr-2 h-4 w-4" />
                  {viewMode === "custom" 
                  ? `${format(dateRange.from, "dd/MM/yy")} - ${format(dateRange.to, "dd/MM/yy")}`
                    : "Custom"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  mode="range"
                  selected={{
                    from: dateRange.from,
                    to: dateRange.to
                  }}
                  onSelect={(range) => {
                    if (range?.from) {
                      setDateRange({
                        from: range.from,
                        to: range.to || range.from
                      });
                      setViewMode("custom");
                    }
                  }}
                  numberOfMonths={2}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-sm text-muted-foreground">
            {viewMode === "month" && `Period: ${format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "dd/MM/yyyy")} - ${format(new Date(), "dd/MM/yyyy")}`}
            {viewMode === "lastMonth" && `Period: ${format(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), "dd/MM/yyyy")} - ${format(new Date(new Date().getFullYear(), new Date().getMonth(), 0), "dd/MM/yyyy")}`}
            {viewMode === "quarter" && `Period: ${format(new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1), "dd/MM/yyyy")} - ${format(new Date(), "dd/MM/yyyy")}`}
            {viewMode === "year" && `Period: ${format(new Date(new Date().getFullYear(), 0, 1), "dd/MM/yyyy")} - ${format(new Date(), "dd/MM/yyyy")}`}
            {viewMode === "custom" && `Period: ${format(dateRange.from, "dd/MM/yyyy")} - ${format(dateRange.to, "dd/MM/yyyy")}`}
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">💰 Revenue Dashboard</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Period Revenue</CardTitle>
              <div className="rounded-full bg-primary/10 p-2">
                <DollarSign className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${metrics.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground">Approved/confirmed proposals</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Period Revenue</CardTitle>
              <div className="rounded-full bg-primary/10 p-2">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-3xl font-bold tracking-tight">
                ${metrics.adjustedPeriodRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">Net revenue excluding external cancellations</p>
              <p className="text-[11px] text-muted-foreground">
                External cancellations removed: ${metrics.uncontrollableCanceledValue.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({metrics.uncontrollableCancellations})
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full -mr-12 -mt-12" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Ticket</CardTitle>
              <div className="rounded-full bg-primary/10 p-2">
                <ShoppingCart className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${metrics.avgTicket.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground">Per sold proposal</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-destructive/5 rounded-full -mr-12 -mt-12" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Lost Value</CardTitle>
              <div className="rounded-full bg-destructive/10 p-2">
                <TrendingDown className="h-4 w-4 text-destructive" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">${metrics.lostValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              <p className="text-xs text-muted-foreground">In Progress + Sent</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">🎯 Sales Conversion Dashboard</h3>
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Conversion</CardTitle>
              <div className="rounded-full bg-primary/10 p-2">
                <Target className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                {metrics.overallConversion.toFixed(1)}%
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                <span className="font-semibold text-foreground">{metrics.convertedCount}</span> of{" "}
                <span className="font-semibold text-foreground">{metrics.totalProposals}</span> proposals
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" />
            <CardHeader>
              <CardTitle className="text-sm font-medium">By Salesperson</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {conversionBySalesValue.slice(0, 3).map(([person, data]) => (
                  <div key={person} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground truncate max-w-[60%]">{person}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-300"
                          style={{ width: `${Math.min(data.rate, 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold text-sm min-w-[3rem] text-right">{data.rate.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" />
            <CardHeader>
              <CardTitle className="text-sm font-medium">By Client Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(metrics.conversionByClientType).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="capitalize text-sm text-muted-foreground">
                      {type.replace('_', ' ')}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-300"
                          style={{ width: `${Math.min(data.rate, 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold text-sm min-w-[3rem] text-right">{data.rate.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full -mr-16 -mt-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">External Cancellations</CardTitle>
              <div className="rounded-full bg-amber-500/10 p-2">
                <TrendingDown className="h-4 w-4 text-amber-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-700">
                {metrics.uncontrollableCancellations}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {metrics.uncontrollableCancellationRate.toFixed(1)}% of total in period
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">💎 Top Clients (Highest Spending)</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/5 rounded-full -ml-16 -mb-16" />
            <CardHeader>
              <CardTitle className="text-sm font-medium">Top 3 Agencies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {metrics.topAgencies.length > 0 ? (
                  metrics.topAgencies.map((agency, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm p-2 rounded-lg hover:bg-primary/5 transition-colors">
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{idx + 1}. {agency.name}</span>
                        <span className="text-xs text-muted-foreground/70">{agency.count} proposals</span>
                      </div>
                      <span className="font-semibold">${agency.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No data available</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/5 rounded-full -ml-16 -mb-16" />
            <CardHeader>
              <CardTitle className="text-sm font-medium">Top 3 Operators</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {metrics.topOperators.length > 0 ? (
                  metrics.topOperators.map((operator, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm p-2 rounded-lg hover:bg-primary/5 transition-colors">
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{idx + 1}. {operator.name}</span>
                        <span className="text-xs text-muted-foreground/70">{operator.count} proposals</span>
                      </div>
                      <span className="font-semibold">${operator.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No data available</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/5 rounded-full -ml-16 -mb-16" />
            <CardHeader>
              <CardTitle className="text-sm font-medium">Top 3 Final Clients</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {metrics.topFinalClients.length > 0 ? (
                  metrics.topFinalClients.map((client, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm p-2 rounded-lg hover:bg-primary/5 transition-colors">
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{idx + 1}. {client.name}</span>
                        <span className="text-xs text-muted-foreground/70">{client.count} proposals</span>
                      </div>
                      <span className="font-semibold">${client.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">📦 Product Mix (Services Sold)</h3>
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full -mr-10 -mt-10" />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Tours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{metrics.serviceMix.tours}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalServices > 0 ? ((metrics.serviceMix.tours / totalServices) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full -mr-10 -mt-10" />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Transfers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{metrics.serviceMix.transfers}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalServices > 0 ? ((metrics.serviceMix.transfers / totalServices) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full -mr-10 -mt-10" />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Attractions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{metrics.serviceMix.attractions}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalServices > 0 ? ((metrics.serviceMix.attractions / totalServices) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full -mr-10 -mt-10" />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Airport Services</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{metrics.serviceMix.airportServices}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalServices > 0 ? ((metrics.serviceMix.airportServices / totalServices) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-primary/5 rounded-full -mr-10 -mt-10" />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Luxury Experiences</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">{metrics.serviceMix.additionalRequests}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalServices > 0 ? ((metrics.serviceMix.additionalRequests / totalServices) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
};

export default CEODashboard;
